import { useState, useEffect, useCallback } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
// TriggersService and TriggerDetails no longer exist - using local interface
interface TriggerDetails {
  name: string
  table?: string
  definition?: string
  timing?: string
  event?: string
  events?: string | string[]
  schema?: string
  condition?: string
  orientation?: string
  function?: string
  functionName?: string
  enabled?: boolean
  isEnabled?: boolean
  created_date?: string
  comment?: string
  definer?: string
  table_name?: string
}

// Extend the TriggerDetails type with additional fields that might be returned
export interface TriggerDetailsResponse extends TriggerDetails {
  tableName?: string
}
import {
  BoltIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { EditTriggerPanel } from './EditTriggerPanel'


interface TriggerDetailsViewProps {
  connection: DatabaseConnection
  database: string
  triggerName: string
  schema?: string
  openEditPanel?: boolean
  onEditPanelClose?: () => void
}

export function TriggerDetailsView({ connection, database, triggerName, schema, openEditPanel, onEditPanelClose }: TriggerDetailsViewProps) {
  const [triggerDetails, setTriggerDetails] = useState<TriggerDetailsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  const { showToast } = useToast()

  const fetchTriggerDetails = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await AgentProxyService.getTriggerDefinition(
        connection.id,
        database,
        triggerName
      )
      
      // Handle the response which contains trigger definition and metadata
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        const triggerData: Record<string, unknown> = response !== null ? (response as unknown as Record<string, unknown>) : {}
        const metaUnknown = triggerData.metadata
        const metadata: Record<string, unknown> | undefined = (metaUnknown && typeof metaUnknown === 'object' && !Array.isArray(metaUnknown)) ? (metaUnknown as unknown as Record<string, unknown>) : undefined

        const getProp = (obj: unknown, key: string): unknown => {
          if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
            const rec = obj as unknown as Record<string, unknown>
            return key in rec ? rec[key] : undefined
          }
          return undefined
        }

        const tableFinal = typeof metadata?.table_name === 'string'
          ? metadata.table_name
          : ((): string | undefined => {
              const v = getProp(triggerData, 'table_name')
              return typeof v === 'string' ? v : undefined
            })()

        const timingFinal = typeof metadata?.timing === 'string'
          ? metadata.timing
          : ((): string | undefined => {
              const v = getProp(triggerData, 'timing')
              return typeof v === 'string' ? v : undefined
            })()

        const eventFinal = typeof metadata?.event === 'string'
          ? metadata.event
          : ((): string | undefined => {
              const v = getProp(triggerData, 'event')
              return typeof v === 'string' ? v : undefined
            })()

        const schemaFinal = typeof metadata?.schema_name === 'string'
          ? metadata.schema_name
          : ((): string | undefined => {
              const v = getProp(triggerData, 'schema_name')
              return typeof v === 'string' ? v : (schema || undefined)
            })()

        setTriggerDetails({
          name: triggerName,
          definition: typeof triggerData.definition === 'string' ? triggerData.definition : undefined,
          table: tableFinal,
          timing: timingFinal,
          event: eventFinal,
          schema: schemaFinal
        })
      } else {
        console.error('Failed to fetch trigger details: Invalid response format')
        showToast('error', 'Failed to load trigger details')
        setTriggerDetails(null)
      }
    } catch (error) {
      console.error('Failed to fetch trigger details:', error)
      showToast('error', 'Failed to load trigger details')
      setTriggerDetails(null)
    } finally {
      setIsLoading(false)
    }
  }, [connection.id, database, triggerName, schema, showToast])

  useEffect(() => {
    void fetchTriggerDetails()
  }, [fetchTriggerDetails])


  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await AgentProxyService.dropTrigger(
        connection.id,
        database,
        triggerName
      )
      
      showToast('success', `Trigger "${triggerName}" deleted successfully`)
      setShowDeleteConfirm(false)
      // Navigate back or refresh parent
    } catch (error: unknown) {
      console.error('Failed to delete trigger:', error)
      const message = error instanceof Error ? error.message : 'Failed to delete trigger'
      showToast('error', message)
    } finally {
      setIsDeleting(false)
    }
  }, [connection.id, database, triggerName, showToast])

  const handleDeleteClick = useCallback(() => {
    void handleDelete()
  }, [handleDelete])

  const handleEditSuccess = useCallback(() => {
    void fetchTriggerDetails()
  }, [fetchTriggerDetails])

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading trigger details...</p>
        </div>
      </div>
    )
  }

  if (!triggerDetails) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <BoltIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Trigger Not Found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            The trigger "{triggerName}" could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BoltIcon className="w-8 h-8 text-yellow-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {triggerDetails.name || triggerName}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Trigger on {triggerDetails.table_name || triggerDetails.tableName || triggerDetails.table}
                {(triggerDetails.schema || schema) && ` in schema ${triggerDetails.schema || schema}`}
              </p>
            </div>
          </div>
          
          <button type="button"
            onClick={() => { setShowDeleteConfirm(true) }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Drop Trigger
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Trigger Name
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1 font-mono">
                  {triggerDetails.name || triggerName}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Table
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {triggerDetails.table_name || triggerDetails.tableName || triggerDetails.table}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Schema
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {triggerDetails.schema || (connection.type === 'mysql' ? database : 'public')}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Timing
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {triggerDetails.timing || 'N/A'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Events
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {Array.isArray(triggerDetails.events) 
                    ? triggerDetails.events.join(', ')
                    : triggerDetails.event || triggerDetails.events || 'N/A'
                  }
                </p>
              </div>

              {triggerDetails.orientation && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Orientation
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {triggerDetails.orientation}
                  </p>
                </div>
              )}

              {(triggerDetails.function || triggerDetails.functionName) && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Function
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {triggerDetails.function || triggerDetails.functionName}
                  </p>
                </div>
              )}
              
              {(triggerDetails.enabled !== undefined || triggerDetails.isEnabled !== undefined) && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </label>
                  <p className="text-sm mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      (triggerDetails.enabled ?? triggerDetails.isEnabled)
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {(triggerDetails.enabled ?? triggerDetails.isEnabled) ? 'Enabled' : 'Disabled'}
                    </span>
                  </p>
                </div>
              )}
              
              {triggerDetails.created_date && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Created Date
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {new Date(triggerDetails.created_date).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {triggerDetails.condition && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border lg:col-span-3">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Condition
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1 font-mono">
                    {triggerDetails.condition}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Trigger Definition */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <ClockIcon className="w-5 h-5 mr-2" />
              Trigger Definition
            </h3>
            
            {triggerDetails.definition ? (
              <pre className="bg-white dark:bg-gray-700 p-4 rounded border text-sm font-mono text-gray-900 dark:text-white overflow-x-auto max-h-96 overflow-y-auto">
                {triggerDetails.definition}
              </pre>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">
                No definition available
              </p>
            )}
          </div>

          {/* Additional Properties */}
          {(triggerDetails.comment || triggerDetails.definer) && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Additional Properties
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {triggerDetails.definer && (
                  <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Definer
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {triggerDetails.definer}
                    </p>
                  </div>
                )}

                {triggerDetails.comment && (
                  <div className="bg-white dark:bg-gray-700 p-3 rounded border md:col-span-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Comment
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {triggerDetails.comment}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Usage Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Trigger Usage:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>BEFORE triggers can modify NEW values and prevent operations</li>
                  <li>AFTER triggers cannot modify data but can perform logging or notifications</li>
                  <li>Use OLD to reference original row data (UPDATE/DELETE)</li>
                  <li>Use NEW to reference new row data (INSERT/UPDATE)</li>
                  <li>Triggers execute automatically when the specified events occur</li>
                  <li>Multiple triggers on the same event execute in alphabetical order</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Drop Trigger
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Are you sure you want to drop the trigger "{triggerName}"? This action cannot be undone and will remove the automatic behavior from the table.
                </p>
                <div className="flex justify-end gap-3">
                  <button type="button"
                    onClick={() => { setShowDeleteConfirm(false) }}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button type="button"
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDeleting ? 'Dropping...' : 'Drop Trigger'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Edit Trigger Panel */}
    {openEditPanel && onEditPanelClose && (
      <EditTriggerPanel
        connection={connection}
        database={database}
        triggerName={triggerName}
        schema={schema}
        initialTrigger={triggerDetails}
        onClose={onEditPanelClose}
        onSuccess={handleEditSuccess}
      />
    )}
  </>
  )
}
